type CompletionHandoffParams = {
	progressRows: any[];
	autoAnalysisRows: any[];
	loadHistory: () => Promise<any>;
	maxAttempts?: number;
	retryDelayMs?: number;
};

const asArray = (value: any): any[] => (Array.isArray(value) ? value : []);

const completedProgressIds = (progressRows: any[]) =>
	new Set(
		asArray(progressRows)
			.filter(
				(progress: any) =>
					String(progress?.progressStatus || progress?.status || '')
						.trim()
						.toUpperCase() === 'COMPLETED',
			)
			.map((progress: any) => Number(progress?.progressId))
			.filter((progressId: number) => progressId > 0),
	);

export const getCompletedAutoAnalysisProgressRows = (
	progressRows: any[],
	autoAnalysisRows: any[],
): any[] => {
	const currentProgressIds = new Set(
		asArray(autoAnalysisRows)
			.map((row: any) => Number(row?.rowId))
			.filter((progressId: number) => progressId > 0),
	);

	return asArray(progressRows).filter((progress: any) => {
		const progressId = Number(progress?.progressId);
		return completedProgressIds([progress]).has(progressId) && currentProgressIds.has(progressId);
	});
};

export const getAutoAnalysisCompletionSignature = (
	progressRows: any[],
	autoAnalysisRows: any[],
): string => {
	const tradeIdByProgressId = new Map<number, number>();
	asArray(autoAnalysisRows).forEach((row: any) => {
		const progressId = Number(row?.rowId);
		if (progressId > 0) {
			tradeIdByProgressId.set(progressId, Number(row?.tradeId || row?.id) || 0);
		}
	});

	return getCompletedAutoAnalysisProgressRows(progressRows, autoAnalysisRows)
		.map((progress: any) => {
			const progressId = Number(progress?.progressId);
			return {
				progressId,
				tradeId: tradeIdByProgressId.get(progressId) || 0,
			};
		})
		.filter(({ progressId }) => progressId > 0)
		.sort((left, right) => left.progressId - right.progressId || left.tradeId - right.tradeId)
		.map(({ progressId, tradeId }) => `${progressId}:${tradeId}`)
		.join(',');
};

export const getCompletedAutoAnalysisTradeIds = (
	progressRows: any[],
	autoAnalysisRows: any[],
): number[] => {
	const completedIds = completedProgressIds(progressRows);
	return Array.from(
		new Set(
			asArray(autoAnalysisRows)
				.filter((row: any) => completedIds.has(Number(row?.rowId)))
				.map((row: any) => Number(row?.tradeId || row?.id))
				.filter((tradeId: number) => tradeId > 0),
		),
	);
};

export const historyContainsCompletedAutoAnalysis = (
	historyPayload: any,
	progressRows: any[],
	autoAnalysisRows: any[],
): boolean => {
	const expectedTradeIds = getCompletedAutoAnalysisTradeIds(progressRows, autoAnalysisRows);
	if (!expectedTradeIds.length) return false;
	const historyRows = asArray(historyPayload?.searchHistory);
	const availableTradeIds = new Set(
		historyRows
			.flatMap((history: any) => asArray(history?.scopeDocuments))
			.map((document: any) => Number(document?.tradeId))
			.filter((tradeId: number) => tradeId > 0),
	);
	return expectedTradeIds.every((tradeId: number) => availableTradeIds.has(tradeId));
};

const wait = (milliseconds: number) =>
	new Promise((resolve) => window.setTimeout(resolve, Math.max(0, milliseconds)));

export const waitForCompletedAutoAnalysisHistory = async ({
	progressRows,
	autoAnalysisRows,
	loadHistory,
	maxAttempts = 3,
	retryDelayMs = 350,
}: CompletionHandoffParams): Promise<any | null> => {
	const attempts = Math.max(1, maxAttempts);
	for (let attempt = 0; attempt < attempts; attempt += 1) {
		const historyPayload = await loadHistory();
		if (historyContainsCompletedAutoAnalysis(historyPayload, progressRows, autoAnalysisRows)) {
			return historyPayload;
		}
		if (attempt < attempts - 1 && retryDelayMs > 0) {
			await wait(retryDelayMs);
		}
	}
	return null;
};
